import React from 'react'
import ReactMarkdown from 'react-markdown'
import { Bot, User } from 'lucide-react'
import type { AiChatMessage } from '@/redux/slices/aiSlice'
import { ErpDiv, ErpSpan } from '@/components/erp'

interface AiMessageBubbleProps {
  message: AiChatMessage
}

/**
 * One chat bubble. Assistant messages render as (safe-by-default)
 * Markdown; react-markdown does not render raw HTML, so no extra
 * sanitisation is required.
 */
export const AiMessageBubble: React.FC<AiMessageBubbleProps> = ({ message }) => {
  const isAssistant = message.role === 'assistant'

  return (
    <ErpDiv
      className={`flex gap-2 mb-3 ${isAssistant ? 'justify-start' : 'justify-end'}`}
      dir="auto"
    >
      {isAssistant && (
        <ErpDiv className="mt-1 shrink-0 rounded-full bg-primary/10 p-2 text-primary">
          <Bot className="h-4 w-4" />
        </ErpDiv>
      )}

      <ErpDiv
        className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm leading-relaxed ${
          isAssistant
            ? 'rounded-tl-sm bg-muted text-foreground'
            : 'rounded-tr-sm bg-primary text-primary-foreground'
        }`}
      >
        {isAssistant ? (
          <ErpDiv className="prose prose-sm max-w-none [&_table]:text-xs [&_table]:w-full">
            <ReactMarkdown>{message.content || '…'}</ReactMarkdown>
          </ErpDiv>
        ) : (
          <ErpSpan className="whitespace-pre-wrap">{message.content}</ErpSpan>
        )}
      </ErpDiv>

      {!isAssistant && (
        <ErpDiv className="mt-1 shrink-0 rounded-full bg-primary p-2 text-primary-foreground">
          <User className="h-4 w-4" />
        </ErpDiv>
      )}
    </ErpDiv>
  )
}

export default AiMessageBubble
